import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import classNames from "classnames";

// Actions
import {
  errorResponse,
  addNewLocation,
  setVirtualKeyboard,
  discardStudy,
  exitAddNewLocation,
  setTempLocationIncludedMaps,
  addTempLocation,
  removeTempLocation,
} from "../../actions";

// MUI Components
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import GridList from "@material-ui/core/GridList";
import GridListTile from "@material-ui/core/GridListTile";
import Badge from "@material-ui/core/Badge";

// Icons
import RemoveIcon from "@material-ui/icons/Remove";
import AddIcon from "@material-ui/icons/Add";
import ClearIcon from "@material-ui/icons/Clear";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

// Other Files
import {
  circularNewLocRegion,
  tempLocationId,
  customFaceMapId,
  faceMapId,
  frontBodyMapId,
  backBodyMapId,
} from "../../constants/constants";
import { colors } from "../../constants/colors";
import { customTheme } from "../../constants/customTheme";
import VirtualKeyboard from "../Shared/VirtualKeyboard";
import Dropzone from "../Shared/Dropzone";

const useStyles = makeStyles({
  root: (props) => ({
    backgroundColor: colors[props.theme].bkg2,
    display: "flex",
    flex: "1 1 auto",
    maxWidth: "100%",
    maxHeight: "100%",
    alignItems: "center",
    flexDirection: "column",
    position: "relative",
    overflowY: "auto",
    ...customTheme(props.theme).customScrollBar,
  }),
  outDiv: {
    flex: "1 1 auto",
    height: "100%",
    // zIndex: 1,
    width: "100%",
  },
  headerSpace: {
    height: "35px",
  },
  locationNameRow: {
    display: "flex",
    alignItems: "center",
    height: "15%",
    "& .MuiFormControl-root": {
      width: "180px",
    },
  },
  orDiv: {
    margin: "30px 35px 0px 35px",
  },
  divider: {
    width: "97%",
    marginTop: "25px",
    marginBottom: "25px",
    backgroundColor: colors.disabledColor,
  },
  sampleNumberContainer: {
    width: "100%",
    display: "flex",
  },
  sampleNumber: {
    margin: "15px",
    width: "100%",
    textAlign: "center",
  },
  sampleNumberLabel: {
    marginRight: "5px",
    marginLeft: "5px",
    textAlignLast: "center",
    width: "100%",
    maxWidth: "100px",
    "& input[type=number]::-webkit-inner-spin-button": {
      // remove default number arrows
      "-webkit-appearance": "none",
    },
  },
  numberInput: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "flex-end",
  },
  numberInputBtn: (props) => ({
    minWidth: "unset",
    width: "30px",
    height: "32px",
    padding: "unset",
    ...customTheme(props.theme).buttonOutlined,
  }),
  paramControl: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    "& .MuiFormControl-root": {
      width: "190px",
    },
  },

  title: {
    ...customTheme().card.title,
    fontSize: "1.5rem",
    fontWeight: 600,
    alignSelf: "center",
    paddingTop: "13px",
    paddingBottom: "0px",
  },
  mapControl: {
    flexDirection: "row",
    justifyContent: "center",
    width: "100%",
  },
  presetSelect: {
    display: "flex",
    minHeight: 0,
  },
  menuScroll: (props) => ({
    "& .MuiMenu-paper": {
      ...customTheme(props.theme).customScrollBar,
    },
  }),
  checkbox: {
    padding: "0px 9px 0px 9px",
  },
  acqInstructionContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  accordian: {
    width: "90%",
    "& .MuiAccordionSummary-content": {
      maxWidth: "calc(100% - 36px)",
    },
  },
  heading: {
    width: "60%",
  },
  secondaryHeading: {
    color: colors.secondaryText,
    maxWidth: "40%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  buttonContainer: {
    ...customTheme().card.buttonContainer,
    marginBottom: "70px",
  },
  buttonProgress: (props) => customTheme(props.theme).buttonProgress,
  buttonPrimary: (props) => customTheme(props.theme).buttonPrimary,
  buttonOutlined: (props) => customTheme(props.theme).buttonOutlined,
  dialogRoot: {
    width: "80%",
    height: " 80%",
    padding: "25px",
    overflow: "hidden",
    maxWidth: "900px",
    alignItems: "center",
  },
  dialogTitle: customTheme().dialog.title,
  dialogButtonContainer: customTheme().dialog.buttonContainer,
  dialogContentContainer: {
    ...customTheme().dialog.contentContainer,
    padding: "0px",
    width: "100%",
    alignItems: "center",
    overflow: "overlay",
  },
  illustrationImage: {
    maxHeight: "100%",
    maxWidth: "100%",
  },
  illustrationFormControl: {
    margin: "15px 0px 15px 0px",
    "& .MuiIconButton-root": {
      padding: "0px",
      marginRight: "10px",
    },
  },
  iconButton: (props) => customTheme(props.theme).iconButton,
  badge: (props) => ({
    height: "100%",
    "& .MuiBadge-badge": {
      ...customTheme(props.theme).iconButton,
      height: "22px",
      width: "22px",
      cursor: "pointer",
      top: "12px",
      right: "12px",
    },
  }),
  gridListTile: {
    height: "100%",
    width: "100%",
  },
  locationTextField: {
    marginTop: "22px",
  },
});

const AddNewLocation = (props) => {
  const classes = useStyles(props);

  const initialState = () => {
    let initialState = {
      locationName: "",
      locationId: "",
      maps: {},
      instruction: { text: "", illustration: [] },
    };
    for (let type in props.acqTypes) {
      initialState = {
        ...initialState,
        [type]: {
          samplesNeeded: 0,
        },
      };
    }
    if (Object.keys(props.parameters).length === 1) {
      //if there is only one parameter then chose that parameter as default
      initialState = {
        ...initialState,
        parameterId: Object.keys(props.parameters)[0],
      };
    } else {
      initialState = {
        ...initialState,
        parameterId: "",
      };
    }

    let mapDict = {};
    for (let map in props.maps) {
      mapDict[map] = true;
    }
    initialState.maps = mapDict;
    return initialState;
  };

  const [newLocation, setLocation] = React.useState(initialState());
  const [presetLocId, setPresetLocId] = React.useState("");
  const [symmetryLocation, setSymmetryLocation] = React.useState(false);
  const [isExpanded, setExpanded] = React.useState(false);
  const [dropzoneDialog, setDropzoneDialog] = React.useState({
    open: false,
    image: null,
  });
  const [keyboardInput, setKeyboardInput] = React.useState({
    inputName: null,
    inputUpdate: { locationName: "", instruction: "" },
  });

  const handleReset = () => {
    // Reset add new location form
    setLocation(initialState());
    setPresetLocId("");
    setExpanded(false);
    props.setVirtualKeyboard(false);
  };

  const handleFormChange = (input_value) => (event) => {
    switch (input_value.field) {
      case "locationName":
        let newLocationName = event.target ? event.target.value : event;
        const id = newLocationName
          .replace(/[^\w\s]/gi, "")
          .replace(/ /g, "_")
          .toUpperCase(); //remove special characters (anything thats not a letter), replace spaces with underscore, turn all letters upper case

        setLocation({
          ...newLocation,
          [input_value.field]: newLocationName,
          locationId: id,
        });

        if (event.target) {
          // Update virtualKeyboard with the manually entered values
          setKeyboardInput({
            ...keyboardInput,
            inputUpdate: { [input_value.field]: event.target.value },
          });
        }

        break;
      case "map":
        setLocation({
          ...newLocation,
          maps: {
            ...newLocation.maps,
            [input_value.type]: !newLocation.maps[input_value.type],
          },
        });
        let newMapSet = {
          ...newLocation.maps,
          [input_value.type]: !newLocation.maps[input_value.type],
        };
        // Change the redux state of the included maps for the temporary location
        for (let mapId in newMapSet) {
          if (
            newMapSet[mapId] &&
            Object.keys(props.protocolLocations[tempLocationId].maps).includes(
              mapId
            )
          ) {
            if (
              presetLocId !== "" &&
              props.presetLocations[presetLocId].maps[mapId]
            ) {
              newMapSet[mapId] = props.presetLocations[presetLocId].maps[mapId];
            } else {
              newMapSet[mapId] =
                props.protocolLocations[tempLocationId].maps[mapId];
            }
          } else if (newMapSet[mapId]) {
            if (
              presetLocId !== "" &&
              props.presetLocations[presetLocId].maps[mapId]
            ) {
              newMapSet[mapId] = props.presetLocations[presetLocId].maps[mapId];
            } else {
              newMapSet[mapId] = circularNewLocRegion;
            }
          } else {
            delete newMapSet[mapId];
          }
        }
        props.setTempLocationIncludedMaps(tempLocationId, newMapSet);
        break;
      case "parameterId":
        setLocation({
          ...newLocation,
          [input_value.field]: event.target.value,
        });
        break;
      case "samplesNeeded":
        setLocation({
          ...newLocation,
          [input_value.type]: {
            ...newLocation[input_value.type],
            [input_value.field]: event.target.value,
          },
        });
        break;
      case "instruction":
        let insText = event.target ? event.target.value : event;
        setLocation({
          ...newLocation,
          [input_value.field]: {
            ...newLocation[input_value.field],
            text: insText,
          },
        });
        break;
      default:
        console.log("Error: input_value field does not exist");
    }
  };

  const handleUpdateInstructionImage = (image) => {
    let imageAlreadyIncluded = false;
    let newImageArray = [...newLocation.instruction.illustration].filter(
      (imageDict) => {
        if (imageDict.image === image) {
          imageAlreadyIncluded = true;
          return false;
        } else {
          return true;
        }
      }
    );
    if (!imageAlreadyIncluded) {
      newImageArray.push({ image: image });
    }
    setLocation({
      ...newLocation,
      instruction: {
        ...newLocation.instruction,
        illustration: newImageArray,
      },
    });
  };

  const handlePresetSelect = (event) => {
    if (event.target.value === "") {
      // Unselect the preset location
      handleReset();
      // Change temp loc map region to default circular
      let mapSet = {};
      for (let map in props.maps) {
        mapSet[map] = circularNewLocRegion;
      }
      props.setTempLocationIncludedMaps(tempLocationId, mapSet);
    } else {
      setPresetLocId(event.target.value);
      let preset = Object.assign({}, props.presetLocations[event.target.value]);
      let mapBoolean = {};
      let mapCoordinates = {};
      for (let map in props.maps) {
        if (map in preset["maps"]) {
          mapCoordinates[map] = preset["maps"][map];
          mapBoolean[map] = true;
        } else {
          mapBoolean[map] = false;
        }
      }
      // change temp loc map region to the preset locations polygon
      props.setTempLocationIncludedMaps(tempLocationId, mapCoordinates);

      if (!Object.keys(props.parameters).includes(preset["parameterId"])) {
        preset["parameterId"] = "";
      }
      setLocation({ ...preset, maps: mapBoolean });
      if (symmetryLocation) {
        handleSymmteryClick({ target: { checked: false } });
      }
    }
  };

  const handleInputCheck = () => {
    let error = { state: false, message: "" };

    if (props.locationIds.includes(newLocation.locationId)) {
      error = {
        state: true,
        message: "Location ID is taken. Enter a different name",
      };
    } else if (props.locationNames.includes(newLocation.locationName)) {
      error = {
        state: true,
        message: "Location name is taken. Enter a different name",
      };
    }

    for (let type in props.acqTypes) {
      if (!Number.isInteger(Number(newLocation[type].samplesNeeded))) {
        error = { state: true, message: "Samples needed should be an integer" };
      }
    }
    return error;
  };

  const handleSubmit = (continueSubmit) => {
    let error = handleInputCheck();
    if (error.state === true) {
      props.errorResponse(error.message);
    } else {
      props.addNewLocation(newLocation, symmetryLocation);
      setSymmetryLocation(false);
      if (continueSubmit) {
        props.addTempLocation();
        handleReset();
      } else {
        props.exitAddNewLocation();
      }
    }
  };

  const handleClose = () => {
    handleReset();
    if (props.createProtocolMode) {
      props.discardStudy("QUIT");
    } else {
      props.exitAddNewLocation();
    }
  };

  const handleButtonDisable = () => {
    let disable = false;
    for (let type in props.acqTypes) {
      if (newLocation[type].samplesNeeded.length === 0) {
        disable = true;
      }
    }
    if (
      newLocation.locationName.length === 0 ||
      newLocation.locationName === tempLocationId ||
      newLocation.locationId.length === 0 ||
      newLocation.parameterId.length === 0
    ) {
      disable = true;
    }
    return disable;
  };

  const handleIncrement = (type, direction) => {
    if (direction === "INCREASE") {
      setLocation({
        ...newLocation,
        [type]: {
          ...newLocation[type],
          samplesNeeded: newLocation[type].samplesNeeded + 1,
        },
      });
    } else if (direction === "DECREASE") {
      setLocation({
        ...newLocation,
        [type]: {
          ...newLocation[type],
          samplesNeeded: newLocation[type].samplesNeeded - 1,
        },
      });
    }
  };

  const handleSymmteryClick = (e) => {
    if (e.target.checked) {
      props.addTempLocation(true);
    } else {
      props.removeTempLocation(true);
    }
    setSymmetryLocation(!symmetryLocation);
  };

  const handleAccordianExpansion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <div className={classes.outDiv}>
      <div className={classes.headerSpace} />
      <div className={classes.root}>
        {/* TITLE */}
        <Typography
          variant="h4"
          className={classes.title}
          color="textPrimary"
          align="center"
        >
          Add New Location
        </Typography>

        {/* PRESET LOCATION SELECTION */}
        <div className={classes.locationNameRow}>
          <FormControl>
            <InputLabel>Preset Location</InputLabel>
            <Select
              className={classes.presetSelect}
              onChange={handlePresetSelect}
              value={presetLocId}
              MenuProps={{ className: classNames(classes.menuScroll) }}
            >
              <MenuItem value="">
                <em>
                  {props.presetLocationsFetching
                    ? "Loading preset locations..."
                    : !props.presetLocationsFetching &&
                      props.presetLocationsReceived &&
                      Object.keys(props.presetLocations).length === 0
                    ? "No preset locations are available"
                    : "None"}
                </em>
              </MenuItem>

              {Object.keys(props.presetLocations).map((location) => (
                <MenuItem key={location} value={location}>
                  {props.presetLocations[location]["locationName"]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <p className={classes.orDiv}>OR</p>

          {/* CUSTOM LOCATION NAME TEXTFIELD */}
          <TextField
            required
            className={classes.locationTextField}
            label="Custom Location Name"
            helperText={"Location ID: " + newLocation.locationId}
            value={newLocation.locationName}
            onFocus={() => {
              setKeyboardInput({
                ...keyboardInput,
                inputName: "locationName",
              });
              props.setVirtualKeyboard(true);
            }}
            onChange={handleFormChange({ type: null, field: "locationName" })}
          />
        </div>

        <Divider className={classes.divider} />

        {/* REQUIRED SAMPLE NUMBER */}
        <Accordion
          className={classes.accordian}
          expanded={isExpanded === "acqType"}
          onChange={handleAccordianExpansion("acqType")}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className={classes.heading}>
              Required number of samples
            </Typography>
            <Typography className={classes.secondaryHeading}>
              {Object.keys(props.acqTypes).map((type) => {
                if (Object.keys(props.acqTypes).indexOf(type) === 0) {
                  return `${props.acqTypes[type].name}: ${newLocation[type].samplesNeeded}`;
                } else {
                  return `, ${props.acqTypes[type].name}: ${newLocation[type].samplesNeeded}`;
                }
              })}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.sampleNumberContainer}>
              {Object.keys(props.acqTypes).map((type) => {
                return (
                  <div key={type} className={classes.sampleNumber}>
                    <Typography>{props.acqTypes[type]["name"]}</Typography>
                    <div className={classes.numberInput}>
                      <Button
                        className={classes.numberInputBtn}
                        disableRipple
                        variant="outlined"
                        disabled={newLocation[type].samplesNeeded === 0}
                        onClick={() => handleIncrement(type, "DECREASE")}
                      >
                        <RemoveIcon />
                      </Button>
                      <TextField
                        className={classes.sampleNumberLabel}
                        type="number"
                        InputProps={{ inputProps: { min: 0, max: 100 } }}
                        value={newLocation[type].samplesNeeded}
                        onChange={handleFormChange({
                          type: type,
                          field: "samplesNeeded",
                        })}
                        onFocus={() => {
                          setKeyboardInput({
                            ...keyboardInput,
                            inputName: null,
                          });
                          props.setVirtualKeyboard(false);
                        }}
                      />
                      <Button
                        className={classes.numberInputBtn}
                        disableRipple
                        variant="outlined"
                        onClick={() => handleIncrement(type, "INCREASE")}
                      >
                        <AddIcon />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </AccordionDetails>
        </Accordion>

        {/* PARAMETER SELECTION */}
        <Accordion
          className={classes.accordian}
          expanded={isExpanded === "parameter"}
          onChange={handleAccordianExpansion("parameter")}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className={classes.heading}>Parameter *</Typography>
            <Typography className={classes.secondaryHeading}>
              {newLocation.parameterId}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.paramControl}>
              <FormControl>
                <InputLabel>Parameter</InputLabel>
                <Select
                  className={classes.select}
                  onChange={handleFormChange({
                    type: null,
                    field: "parameterId",
                  })}
                  value={newLocation.parameterId}
                  onFocus={() => {
                    setKeyboardInput({
                      ...keyboardInput,
                      inputName: null,
                    });
                    props.setVirtualKeyboard(false);
                  }}
                >
                  {Object.keys(props.parameters).map((parameterId) => {
                    return (
                      <MenuItem key={parameterId} value={parameterId}>
                        {props.parameters[parameterId].name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </div>
          </AccordionDetails>
        </Accordion>

        {/* MAP SELECTION */}
        <Accordion
          className={classes.accordian}
          expanded={isExpanded === "maps"}
          onChange={handleAccordianExpansion("maps")}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className={classes.heading}>Maps</Typography>
            <Typography className={classes.secondaryHeading}>
              {Object.keys(props.maps)
                .filter((mapId) => newLocation.maps[mapId])
                .map((map) => {
                  if (
                    Object.keys(newLocation.maps).includes(customFaceMapId) &&
                    (map === customFaceMapId || map === faceMapId)
                  ) {
                    if (map === customFaceMapId) {
                      if (
                        Object.keys(newLocation.maps)
                          .filter(
                            (mapId) =>
                              newLocation.maps[mapId] && mapId !== faceMapId
                          )
                          .indexOf(map) === 0
                      ) {
                        return `${props.maps[map].name}`;
                      } else {
                        return `, ${props.maps[map].name}`;
                      }
                    }
                  } else {
                    if (
                      Object.keys(newLocation.maps)
                        .filter((mapId) => newLocation.maps[mapId])
                        .indexOf(map) === 0
                    ) {
                      return `${props.maps[map].name}`;
                    } else {
                      return `, ${props.maps[map].name}`;
                    }
                  }
                })}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup className={classes.mapControl}>
              {Object.keys(props.maps).map((map) => {
                if (
                  !(
                    Object.keys(props.maps).includes(customFaceMapId) &&
                    map === faceMapId
                  )
                ) {
                  return (
                    <FormControlLabel
                      key={map}
                      control={
                        <Checkbox
                          color="primary"
                          className={classes.checkbox}
                          onChange={handleFormChange({
                            type: map,
                            field: "map",
                          })}
                          checked={newLocation.maps[map]}
                          value={newLocation.maps[map]}
                        />
                      }
                      label={props.maps[map].name}
                    />
                  );
                }
              })}
            </FormGroup>
          </AccordionDetails>
        </Accordion>

        <Accordion
          className={classes.accordian}
          expanded={isExpanded === "instruction"}
          onChange={handleAccordianExpansion("instruction")}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className={classes.heading}>
              Acquisition instructions
            </Typography>
            <Typography className={classes.secondaryHeading}>
              {newLocation.instruction.text}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.acqInstructionContainer}>
              <TextField
                multiline={true}
                rows="5"
                value={newLocation.instruction.text}
                rowsMax="10" // allows height to expand to this value when notes get long
                onChange={handleFormChange({
                  type: null,
                  field: "instruction",
                })}
                onFocus={() => {
                  setKeyboardInput({
                    ...keyboardInput,
                    inputName: "instruction",
                  });
                  props.setVirtualKeyboard(true);
                }}
                fullWidth={true}
                variant="filled"
              />

              <FormControlLabel
                className={classes.illustrationFormControl}
                control={
                  <IconButton
                    className={classes.iconButton}
                    onClick={() =>
                      setDropzoneDialog({
                        ...dropzoneDialog,
                        open: true,
                        image: null,
                      })
                    }
                  >
                    <AddIcon />
                  </IconButton>
                }
                label="Add illustration"
              />
              <GridList>
                {newLocation.instruction.illustration.map((insImage) => (
                  <GridListTile
                    className={classes.gridListTile}
                    key={newLocation.instruction.illustration.indexOf(insImage)}
                  >
                    <Badge
                      className={classes.badge}
                      onClick={() =>
                        handleUpdateInstructionImage(insImage.image)
                      }
                      badgeContent={<ClearIcon />}
                    >
                      <img
                        className={classes.illustrationImage}
                        src={insImage.image}
                        alt={"acquisition instruction illustration"}
                      />
                    </Badge>
                  </GridListTile>
                ))}
              </GridList>
            </div>
          </AccordionDetails>
        </Accordion>

        {/* SYMMETRICAL LOCATIONS OPTION */}
        {/* This option is presented only if all the maps have bilateral symmetry */}
        {Object.keys(props.maps).every((mapId) =>
          [frontBodyMapId, backBodyMapId, faceMapId, customFaceMapId].includes(
            mapId
          )
        ) && (
          <Accordion
            className={classes.accordian}
            expanded={isExpanded === "symmetry"}
            onChange={handleAccordianExpansion("symmetry")}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <FormControlLabel
                key={"symmetry"}
                onClick={(event) => event.stopPropagation()}
                control={
                  <Checkbox
                    color="primary"
                    className={classes.checkbox}
                    onChange={(e) => handleSymmteryClick(e)}
                    checked={symmetryLocation}
                    value={symmetryLocation}
                    disabled={presetLocId !== ""}
                  />
                }
                label="Create vertically symmetric locations"
              />
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Create two vertically symmetric locations. The locations will
                have the same required number of samples, parameter, and maps.
                The (R)/(L) designation is automatically assigned to the
                location name.
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}

        {/*  BUTTONS  */}
        <div className={classes.buttonContainer}>
          <Button
            className={classes.buttonOutlined}
            onClick={() => handleClose()}
            variant="outlined"
            disabled={props.locationIds.length < 1}
          >
            Cancel
          </Button>

          <Button
            onClick={() => handleSubmit(true)}
            variant="contained"
            color="primary"
            disabled={handleButtonDisable()}
            className={classes.buttonPrimary}
          >
            Add & Add More
          </Button>

          <Button
            onClick={() => handleSubmit(false)}
            variant="contained"
            color="primary"
            disabled={handleButtonDisable()}
            className={classes.buttonPrimary}
          >
            Add & Close
          </Button>
        </div>

        {/* DROPZONE DIALOG */}
        <Dialog
          PaperProps={{ className: classNames(classes.dialogRoot) }}
          open={dropzoneDialog.open}
        >
          <DialogTitle>
            <Typography className={classes.dialogTitle}>
              Add Illustration
            </Typography>
          </DialogTitle>
          <DialogContent className={classes.dialogContentContainer}>
            {dropzoneDialog.image === null ? (
              <Dropzone
                formatError={(errorMessage) =>
                  props.errorResponse(errorMessage)
                }
                upload={(base64String) =>
                  setDropzoneDialog({
                    ...dropzoneDialog,
                    image: base64String,
                  })
                }
                acceptedFormat={[".png", ".jpg"]}
                readFile={true}
              />
            ) : (
              <img
                className={classes.illustrationImage}
                alt={"acquisition instruction illustration"}
                src={dropzoneDialog.image}
              />
            )}
          </DialogContent>

          <DialogActions className={classes.dialogButtonContainer}>
            <Button
              onClick={() =>
                setDropzoneDialog({
                  ...dropzoneDialog,
                  open: false,
                  image: null,
                })
              }
              className={classes.buttonOutlined}
              variant="outlined"
            >
              Cancel
            </Button>
            {dropzoneDialog.image !== null && (
              <Button
                onClick={() => {
                  handleUpdateInstructionImage(dropzoneDialog.image);
                  setDropzoneDialog({
                    ...dropzoneDialog,
                    open: false,
                    image: null,
                  });
                }}
                className={classes.buttonPrimary}
                variant="contained"
                color="primary"
              >
                Add Illustration
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* VIRTUAL KEYBOARD */}
        <VirtualKeyboard
          anchor={"center"}
          keyboardInitialInput={{
            locationName: newLocation.locationName,
            instruction: newLocation.instruction.text,
          }}
          keyboardInputUpdate={keyboardInput.inputUpdate}
          keyboardInputName={keyboardInput.inputName}
          keyboardChange={handleFormChange({
            type: "text",
            field: keyboardInput.inputName,
          })}
        />
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      errorResponse,
      addNewLocation,
      setVirtualKeyboard,
      discardStudy,
      exitAddNewLocation,
      setTempLocationIncludedMaps,
      addTempLocation,
      removeTempLocation,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  let filteredtPresetLocations = {};
  for (const presetLoc in state.availableProtocols.presetLocations) {
    if (!state.locations.allIds.includes(presetLoc)) {
      filteredtPresetLocations[presetLoc] =
        state.availableProtocols.presetLocations[presetLoc];
    }
  }

  let locationNames = [];
  for (let i = 0; i < state.locations.allIds.length; i++) {
    locationNames.push(state.locations.byId[state.locations.allIds[i]].name);
  }

  return {
    presetLocationsFetching: state.availableProtocols.presetLocationsFetching,
    presetLocationsReceived: state.availableProtocols.presetLocationsReceived,
    presetLocations: filteredtPresetLocations,
    protocolLocations: state.protocol.locations,
    locationNames: locationNames,
    locationIds: state.locations.allIds,
    acqTypes: state.protocol.acq_types,
    parameters: state.protocol.parameters,
    maps: state.protocol.maps,
    createProtocolMode: state.utility.mode.createProtocolMode,
    theme: state.utility.theme,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AddNewLocation);
